import { Banner, BlockStack, Checkbox, Form, FormLayout, Text, Modal, Select, TextField, ChoiceList, Spinner, Button } from '@shopify/polaris';
import { DeleteIcon, PageDownIcon, SaveIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notEmpty, useField, useForm } from '@shopify/react-form';
import { useNotification } from 'context/NotificationContext';
import helpers from 'helpers/index';
import __ from 'languages/index';
import { useMutateDeleteOrderTaxInformation, useMutateOrderTaxInformation, useGetOrder, ENUM_ORDER_STATUS } from 'queries/orders.query';
import { TypedUser_tax_information, useGetUserTaxInformations } from 'queries/user_tax_information.query';
import { useSettings } from 'context/SettingsContext';

type BusinessTaxLookupResponse = {
  orgType?: string;
  taxID?: string;
  name?: string | null;
  address?: string | null;
  taxDepartment?: string;
  status?: string;
  updatedAt?: string;
};

type EditOrderTaxModalProps = {
  open: boolean;
  orderId: string | null;
  onClose: (reload?: boolean) => void;
};

export default function EditOrderTaxModal({ open, orderId, onClose }: EditOrderTaxModalProps) {
  const { addNotification } = useNotification();
  const { settings } = useSettings();

  const { mutateAsync: updateOrderTaxInformation, isPending: updatingInvoice } = useMutateOrderTaxInformation();
  const { mutateAsync: deleteOrderTaxInformation, isPending: deletingInvoice } = useMutateDeleteOrderTaxInformation();
  const { data: orderData } = useGetOrder(orderId || '');

  const [selectedTaxInformationId, setSelectedTaxInformationId] = useState<string>('default');
  const [taxLookupError, setTaxLookupError] = useState('');
  const [taxLookupMessage, setTaxLookupMessage] = useState('');
  const [taxLookupLoading, setTaxLookupLoading] = useState(false);
  const taxLookupControllerRef = useRef<AbortController | null>(null);

  const { data: taxInformationData } = useGetUserTaxInformations({
    user_id: orderData?.customer_data?.user_id,
    limit: 50,
    sort: 'createdAt:desc',
  });

  const taxInformations = useMemo<TypedUser_tax_information[]>(() => taxInformationData?.body ?? [], [taxInformationData]);

  const invoiceFields = {
    invoice_tax_type: useField<string>({ value: orderData?.order_invoice_info?.tax_type || 'COMPANY', validates: [] }),
    name: useField<string>({ value: orderData?.order_invoice_info?.name || orderData?.customer_data?.display_name || 'Khách lẻ', validates: [] }),
    buyer_name: useField<string>({
      value: orderData?.order_invoice_info?.buyer_name || '',
      validates: [],
    }),
    invoice_tax_code: useField<string>({ value: orderData?.order_invoice_info?.tax_code || '', validates: [] }),
    invoice_tax_cccd: useField<string>({ value: orderData?.order_invoice_info?.tax_cccd || '', validates: [] }),
    invoice_tax_mamoiquanhengansach: useField<string>({ value: orderData?.order_invoice_info?.tax_mamoiquanhengansach || '', validates: [] }),
    invoice_email: useField<string>({
      value: orderData?.order_invoice_info?.invoice_email || '',
      validates: [notEmpty('Vui lòng nhập email nhận hóa đơn')],
    }),
    invoice_phone: useField<string>({
      value: orderData?.order_invoice_info?.invoice_phone || orderData?.customer_data?.user_phonenumber || '',
      validates: [],
    }),
    invoice_address: useField<string>({ value: orderData?.order_invoice_info?.invoice_address || '', validates: [] }),
    invoice_save_to_invoice_list: useField<string>({ value: '0', validates: [] }),
  };

  const normalizedTaxCode = useMemo(() => invoiceFields.invoice_tax_code.value.trim(), [invoiceFields.invoice_tax_code.value]);

  const applyTaxInformationToFields = useCallback((taxInformation?: TypedUser_tax_information) => {
    invoiceFields.invoice_tax_type.onChange(taxInformation?.tax_type);
    invoiceFields.name.onChange(taxInformation?.name || '');
    invoiceFields.buyer_name.onChange(taxInformation?.buyer_name || '');
    invoiceFields.invoice_tax_code.onChange(taxInformation?.tax_code || '');
    invoiceFields.invoice_tax_cccd.onChange(taxInformation?.tax_cccd || '');
    invoiceFields.invoice_tax_mamoiquanhengansach.onChange(taxInformation?.tax_mamoiquanhengansach || '');
    invoiceFields.invoice_email.onChange(taxInformation?.invoice_email || '');
    invoiceFields.invoice_phone.onChange(taxInformation?.invoice_phone || '');
    invoiceFields.invoice_address.onChange(taxInformation?.invoice_address || '');
  }, []);

  const applyOrderInvoiceInfoToFields = useCallback(() => {
    applyTaxInformationToFields({
      tax_type: orderData?.order_invoice_info?.tax_type || 'COMPANY',
      name: orderData?.order_invoice_info?.name || orderData?.customer_data?.display_name || 'Khách lẻ',
      buyer_name: orderData?.order_invoice_info?.buyer_name || '',
      tax_code: orderData?.order_invoice_info?.tax_code || '',
      tax_cccd: orderData?.order_invoice_info?.tax_cccd || '',
      tax_mamoiquanhengansach: orderData?.order_invoice_info?.tax_mamoiquanhengansach || '',
      invoice_email: orderData?.order_invoice_info?.invoice_email || '',
      invoice_phone: orderData?.order_invoice_info?.invoice_phone || orderData?.customer_data?.user_phonenumber || '',
      invoice_address: orderData?.order_invoice_info?.invoice_address || '',
    });
  }, [applyTaxInformationToFields, orderData]);

  useEffect(() => {
    if (!open || !orderData) return;
    applyOrderInvoiceInfoToFields();
  }, [applyOrderInvoiceInfoToFields, open, orderData]);

  useEffect(() => {
    if (!orderData) return;
    if (helpers.isEmpty(taxInformations)) {
      setSelectedTaxInformationId('default');
      return;
    }
    const orderInvoiceInfo = orderData?.order_invoice_info;
    if (!orderInvoiceInfo) {
      setSelectedTaxInformationId('default');
      return;
    }
    const matchedTaxInformation = taxInformations.find(
      (taxInformation) =>
        String(taxInformation?.id) === String(orderInvoiceInfo?.id) ||
        (!!taxInformation?.tax_code && taxInformation?.tax_code === orderInvoiceInfo?.tax_code)
    );
    setSelectedTaxInformationId(matchedTaxInformation ? String(matchedTaxInformation.id) : 'default');
  }, [orderData, taxInformations]);

  const taxInformationOptions = useMemo(() => {
    const options = taxInformations.map((taxInformation) => {
      const baseLabel = taxInformation?.name || taxInformation?.tax_code || 'Thông tin thuế';
      const labelSuffix = taxInformation?.tax_code ? ` - ${taxInformation.tax_code}` : '';
      const defaultSuffix = taxInformation?.is_default ? ' (Mặc định)' : '';
      return {
        label: `${baseLabel}${labelSuffix}${defaultSuffix}`,
        value: String(taxInformation?.id),
      };
    });
    return [{ label: 'Hóa đơn mặc định', value: 'default' }, ...options];
  }, [taxInformations]);

  const handleTaxInformationChange = useCallback(
    async (value: string) => {
      setSelectedTaxInformationId(value);

      const selectedTaxInformation = taxInformations.find((taxInformation) => String(taxInformation?.id) === value);
      if (!selectedTaxInformation) {
        applyOrderInvoiceInfoToFields();
        return;
      }
      applyTaxInformationToFields(selectedTaxInformation);
    },
    [applyOrderInvoiceInfoToFields, applyTaxInformationToFields, taxInformations]
  );

  const handleTaxTypeChange = useCallback(
    (value: string[]) => {
      const nextTaxType = value[0];
      if (!nextTaxType || nextTaxType === invoiceFields.invoice_tax_type.value) return;

      invoiceFields.invoice_tax_type.onChange(nextTaxType);
      invoiceFields.name.onChange('');
      setTaxLookupError('');
      setTaxLookupMessage('');
    },
    [invoiceFields.invoice_tax_type, invoiceFields.name]
  );

  const handleTaxCodeChange = useCallback(
    (value: string) => {
      if (value !== invoiceFields.invoice_tax_code.value) {
        // The company name belongs to the old tax code (or the personal tab), so
        // it must not be carried over while the new company is being looked up.
        invoiceFields.name.onChange('');
        setSelectedTaxInformationId('default');
      }
      invoiceFields.invoice_tax_code.onChange(value);
    },
    [invoiceFields.invoice_tax_code, invoiceFields.name]
  );

  const fetchBusinessTaxInformation = useCallback(async () => {
    if (!normalizedTaxCode) {
      setTaxLookupError('Vui lòng nhập mã số thuế cần tra cứu.');
      return;
    }

    taxLookupControllerRef.current?.abort();
    const controller = new AbortController();
    taxLookupControllerRef.current = controller;
    const requestedTaxCode = normalizedTaxCode;
    setTaxLookupError('');
    setTaxLookupMessage('');
    setTaxLookupLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/taxfill/tax-payer/${encodeURIComponent(requestedTaxCode)}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = (await response.json()) as BusinessTaxLookupResponse;
      if (!result?.taxID || !result?.name) {
        throw new Error('Không tìm thấy doanh nghiệp tương ứng với mã số thuế đã nhập.');
      }

      if (controller.signal.aborted || invoiceFields.invoice_tax_code.value.trim() !== requestedTaxCode) return;

      let companyAddress = result.address?.trim().replace('TP ', 'Thành phố ') || '';
      if (companyAddress && !companyAddress.includes('Việt Nam')) {
        companyAddress = companyAddress + ', Việt Nam';
      }

      // An explicit lookup is authoritative for the entered tax code.
      invoiceFields.name.onChange(result.name?.trim() || '');
      invoiceFields.invoice_address.onChange(companyAddress);

      setTaxLookupMessage(`Đã tự động điền thông tin công ty từ mã số thuế. ${result.status || ''}`);
    } catch (error: any) {
      if (controller.signal.aborted) return;
      setTaxLookupError(error?.message || 'Không thể tra cứu thông tin mã số thuế. Vui lòng kiểm tra lại.');
    } finally {
      if (taxLookupControllerRef.current === controller) {
        taxLookupControllerRef.current = null;
        setTaxLookupLoading(false);
      }
    }
  }, [invoiceFields.invoice_address, invoiceFields.invoice_tax_code, invoiceFields.name, normalizedTaxCode]);

  useEffect(() => {
    taxLookupControllerRef.current?.abort();
    taxLookupControllerRef.current = null;
    setTaxLookupError('');
    setTaxLookupMessage('');
    setTaxLookupLoading(false);
  }, [normalizedTaxCode, open]);

  useEffect(() => () => taxLookupControllerRef.current?.abort(), []);

  const { submit, submitting, dirty } = useForm({
    fields: invoiceFields,
    async onSubmit(values) {
      if (!orderData?.id) return { status: 'fail', errors: [] };
      try {
        await updateOrderTaxInformation({
          order_id: String(orderData?.id ?? orderId),
          tax_type: values.invoice_tax_type,
          name: values.name,
          buyer_name: values.buyer_name,
          tax_code: values.invoice_tax_code,
          tax_cccd: values.invoice_tax_cccd,
          tax_mamoiquanhengansach: values.invoice_tax_mamoiquanhengansach,
          invoice_email: values.invoice_email,
          invoice_phone: values.invoice_phone,
          invoice_address: values.invoice_address,
          invoice_save_to_invoice_list: Number(values.invoice_save_to_invoice_list),
        });
        addNotification('info', 'Cập nhật thông tin thuế thành công!');
        onClose(true);
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            invoiceFields[e.params.field]?.setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  const deleteInvoiceCallback = useCallback(async () => {
    if (!orderData?.id) return;
    try {
      await deleteOrderTaxInformation(orderData?.id);
      addNotification('info', 'Xoá thông tin thuế thành công!');
      onClose(true);
    } catch (e) {
      console.error(e, 'ERR_DELETE_INVOICE');
      addNotification('error', 'Lỗi xoá thông tin thuế!');
    }
  }, [addNotification, deleteOrderTaxInformation, onClose, orderData]);

  useEffect(() => {
    if (open) return;
    helpers.resetForm(invoiceFields);
  }, [open]);

  const isDisableForm = orderData?.order_status === ENUM_ORDER_STATUS.CANCELLED;
  const isLoading = updatingInvoice || deletingInvoice || submitting;

  return (
    <Modal
      open={open}
      title={`Thông tin thuế đơn hàng ${orderData?.order_code ? `#${orderData?.order_code}` : ''}`.trim()}
      onClose={() => onClose(false)}
      primaryAction={{
        content: 'Cập nhật thông tin thuế',
        icon: SaveIcon,
        disabled: isDisableForm || !dirty,
        loading: isLoading,
        onAction: submit,
      }}
      secondaryActions={[
        {
          destructive: true,
          content: 'Xoá bỏ',
          icon: DeleteIcon,
          disabled: !orderData?.order_invoice_info || isLoading || isDisableForm,
          onAction: deleteInvoiceCallback,
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <BlockStack gap="400">
            {orderData?.order_invoice_info === null && (
              <Select
                label="Profile thuế đã lưu"
                options={taxInformationOptions}
                onChange={handleTaxInformationChange}
                value={selectedTaxInformationId}
                disabled={isLoading || helpers.isEmpty(orderData?.customer_data?.user_id)}
              />
            )}
            <FormLayout>
              {orderData?.order_invoice_info?.is_customer_required === 1 ? (
                <Text as="p" variant="headingSm" fontWeight="bold">
                  ⚠️ Thông tin này đã được người dùng thêm vào, lưu ý khi chỉnh sửa.
                </Text>
              ) : null}

              <ChoiceList
                title="Loại thuế suất"
                choices={[
                  {
                    label: 'Hóa đơn cá nhân',
                    value: 'PERSONAL',
                  },
                  {
                    label: 'Hóa đơn doanh nghiệp',
                    value: 'COMPANY',
                  },
                ]}
                selected={[invoiceFields.invoice_tax_type.value]}
                onChange={handleTaxTypeChange}
              />

              {invoiceFields.invoice_tax_type.value === 'PERSONAL' ? (
                <BlockStack gap="100">
                  <TextField
                    autoComplete="off"
                    label="Số căn cước công dân"
                    readOnly={isLoading || isDisableForm}
                    {...invoiceFields.invoice_tax_cccd}
                  />
                  <TextField
                    autoComplete="off"
                    label="Tên trên Căn cước"
                    requiredIndicator
                    readOnly={isLoading || isDisableForm}
                    {...invoiceFields.name}
                  />
                </BlockStack>
              ) : (
                <BlockStack gap="100">
                  <TextField
                    autoComplete="off"
                    label="Mã số thuế"
                    clearButton
                    onClearButtonClick={() => {
                      handleTaxCodeChange('');
                    }}
                    disabled={isLoading || isDisableForm}
                    connectedRight={
                      <Button onClick={() => fetchBusinessTaxInformation()} icon={PageDownIcon} disabled={isLoading || isDisableForm}>
                        Lấy thông tin
                      </Button>
                    }
                    {...invoiceFields.invoice_tax_code}
                    onChange={handleTaxCodeChange}
                  />
                  {taxLookupLoading ? (
                    <Text as="p" variant="bodySm" tone="subdued">
                      <Spinner size="small" /> Đang tra cứu thông tin doanh nghiệp...
                    </Text>
                  ) : null}
                  {taxLookupError ? <Banner tone="critical">{taxLookupError}</Banner> : null}
                  {taxLookupMessage ? <Banner tone="success">{taxLookupMessage}</Banner> : null}
                  <TextField
                    autoComplete="off"
                    label="Mã quan hệ ngân sách"
                    disabled={isLoading || isDisableForm}
                    {...invoiceFields.invoice_tax_mamoiquanhengansach}
                  />
                  <TextField autoComplete="off" label="Tên người mua" disabled={isLoading || isDisableForm} {...invoiceFields.buyer_name} />

                  <TextField
                    autoComplete="off"
                    label="Tên công ty"
                    requiredIndicator
                    disabled={isLoading || isDisableForm}
                    {...invoiceFields.name}
                    multiline={3}
                  />
                </BlockStack>
              )}
              <TextField
                autoComplete="off"
                label="Địa chỉ xuất hóa đơn"
                multiline={3}
                disabled={isLoading || isDisableForm}
                {...invoiceFields.invoice_address}
              />
              <FormLayout.Group>
                <TextField
                  autoComplete="off"
                  label="Email nhận hóa đơn"
                  requiredIndicator
                  disabled={isLoading || isDisableForm}
                  {...invoiceFields.invoice_email}
                />
                <TextField autoComplete="off" label="Số điện thoại" disabled={isLoading || isDisableForm} {...invoiceFields.invoice_phone} />
              </FormLayout.Group>
              {!orderData?.order_invoice_info ? (
                <Checkbox
                  label="Lưu vào danh sách hóa đơn, sử dụng cho các đơn hàng sau"
                  checked={invoiceFields.invoice_save_to_invoice_list.value === '1'}
                  onChange={(value) => {
                    invoiceFields.invoice_save_to_invoice_list.onChange(String(Number(value)));
                  }}
                  disabled={isLoading || isDisableForm}
                />
              ) : null}
            </FormLayout>
          </BlockStack>
        </Form>
      </Modal.Section>
    </Modal>
  );
}

